import { useState, useEffect } from 'react';
import axios from 'axios';

const emojis = [
    '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂',
    '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩',
    '😘', '😗', '😚', '😋', '😛', '😜', '🤪', '😝',
    '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐',
    '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌',
    '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢',
    '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠',
    '🥳', '😎', '🤓', '🧐', '👍', '👎', '👏', '🙌',
    '🤝', '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤',
    '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘'
];

export default function Guestbook() {
    const [messages, setMessages] = useState([]);
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [jumpPage, setJumpPage] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const pageSize = 10;

    // 获取当前用户
    const getCurrentUser = () => {
        try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
                return JSON.parse(userStr);
            }
        } catch (e) {
            console.error('获取用户信息失败', e);
        }
        return null;
    };

    // 获取留言列表
    const fetchMessages = async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * pageSize;
            const res = await axios.get(`/api/guestbook/list?limit=${pageSize}&offset=${offset}`);
            if (res.data.success) {
                setMessages(res.data.data.list);
                setTotalCount(res.data.data.total);
                setTotalPages(Math.ceil(res.data.data.total / pageSize));
                setCurrentPage(page);
            }
        } catch (err) {
            console.error('获取留言失败:', err);
            alert('获取留言失败，请稍后重试');
        } finally {
            setLoading(false);
        }
    };

    // 插入表情
    const insertEmoji = (emoji) => {
        setContent(prev => prev + emoji);
        setShowEmojiPicker(false);
    };

    // 跳转页面
    const handleJump = () => {
        const page = parseInt(jumpPage);
        if (isNaN(page) || page < 1 || page > totalPages) {
            alert(`请输入1到${totalPages}之间的数字`);
            return;
        }
        fetchMessages(page);
        setJumpPage('');
    };

    // 生成分页页码数组（带省略）
    const getPageNumbers = () => {
        const pages = [];
        const showCount = 5; // 最多显示几个页码
        
        if (totalPages <= showCount + 2) {
            // 页数不多，全部显示
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // 页数多，需要省略
            if (currentPage <= 3) {
                // 当前页靠近开头
                for (let i = 1; i <= showCount; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                // 当前页靠近结尾
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - showCount + 2; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // 当前页在中间
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    // 提交留言
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!content.trim()) {
            alert('请输入留言内容');
            return;
        }
        try {
            setSubmitting(true);
            const user = getCurrentUser();
            const username = user?.username || null;
            const res = await axios.post('/api/guestbook/submit', { username, content: content.trim() });
            if (res.data.success) {
                alert('留言提交成功！');
                setContent('');
                fetchMessages(1);
            } else {
                alert(res.data.message || '提交失败，请稍后重试');
            }
        } catch (err) {
            console.error('提交留言失败:', err);
            alert(err.response?.data?.message || '提交失败，请稍后重试');
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        fetchMessages(1);
    }, []);

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-8 text-center">留言簿</h1>
            
            {/* 提交留言表单 */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <form onSubmit={handleSubmit}>
                    <div className="mb-4 relative">
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-32 resize-none pr-12"
                            placeholder="请输入您的留言内容（最多1000字）"
                            maxLength={1000}
                        />
                        <button
                            type="button"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            className="absolute right-3 bottom-3 text-2xl hover:scale-110 transition"
                            title="添加表情"
                        >
                            😊
                        </button>
                        
                        {/* 表情选择器 */}
                        {showEmojiPicker && (
                            <div className="absolute right-0 top-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-10 max-w-xs">
                                <div className="grid grid-cols-8 gap-1">
                                    {emojis.map((emoji, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => insertEmoji(emoji)}
                                            className="text-xl p-1 hover:bg-gray-100 rounded transition"
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex gap-3">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition disabled:bg-gray-400"
                        >
                            {submitting ? '提交中...' : '提交'}
                        </button>
                        <a href="/" className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg transition flex items-center">
                            返回
                        </a>
                    </div>
                </form>
            </div>

            {/* 留言列表 */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    留言列表 ({totalCount}条)
                </h2>
                {loading ? (
                    <div className="text-center py-8 text-gray-500">加载中...</div>
                ) : messages.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">暂无留言，快来发表第一条留言吧！</div>
                ) : (
                    <>
                        <div>
                            {messages.map((msg, index) => (
                                <div key={msg.id} className={`${index > 0 ? 'pt-6 border-t-2 border-gray-300' : ''} pb-4`}>
                                    <div className="flex justify-between items-center mb-2">
                                        <span className={`font-medium text-sm ${msg.nickname === '匿名访客' ? 'text-gray-500' : 'text-blue-600'}`}>
                                            {msg.nickname}
                                        </span>
                                        <span className="text-sm text-gray-500">{msg.created_at}</span>
                                    </div>
                                    <hr className="border-gray-200 mb-2" />
                                    <p className="text-black font-bold whitespace-pre-wrap text-base mb-3" style={{fontFamily: '"KaiTi", "楷体", STKaiti, serif'}}>{msg.content}</p>
                                    
                                    {/* 站长回复 */}
                                    {msg.reply && (
                                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r mb-2">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-sm font-bold text-blue-800">站长回复</span>
                                                <span className="text-xs text-blue-600">{msg.reply_at}</span>
                                            </div>
                                            <p className="text-sm text-blue-900 whitespace-pre-wrap" style={{fontFamily: '"KaiTi", "楷体", STKaiti, serif'}}>{msg.reply}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* 分页控件 */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-6 pt-6 border-t border-gray-200 flex-wrap">
                                <button
                                    onClick={() => fetchMessages(currentPage - 1)}
                                    disabled={currentPage <= 1}
                                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    上一页
                                </button>
                                {getPageNumbers().map((page, i) => (
                                    page === '...' ? (
                                        <span key={`ellipsis-${i}`} className="px-2 text-gray-500">...</span>
                                    ) : (
                                        <button
                                            key={page}
                                            onClick={() => fetchMessages(page)}
                                            className={`px-3 py-1 rounded ${currentPage === page ? 'bg-blue-500 text-white' : 'border border-gray-300 hover:bg-gray-100'}`}
                                        >
                                            {page}
                                        </button>
                                    )
                                ))}
                                <button
                                    onClick={() => fetchMessages(currentPage + 1)}
                                    disabled={currentPage >= totalPages}
                                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    下一页
                                </button>
                                <span className="ml-4 text-gray-500 text-sm">
                                    第 {currentPage}/{totalPages} 页
                                </span>
                                <div className="flex items-center gap-2 ml-2">
                                    <input
                                        type="number"
                                        min="1"
                                        max={totalPages}
                                        value={jumpPage}
                                        onChange={(e) => setJumpPage(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
                                        placeholder="页码"
                                    />
                                    <button
                                        onClick={handleJump}
                                        className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded transition"
                                    >
                                        跳转
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
